import { useEffect, useMemo, useState } from "react";
import { ToastContainer, Slide, toast } from "react-toastify";
import { useRouter } from "next/router";
import { NavigationDrawer } from "@ecologi/react-components/src/NavigationDrawer";
import { parseQueryObject } from "../../lib";
import { useStore } from "../../store";
import { getNavSectionsForMarketingSite } from "./get-nav-items-for-marketing-site";
import { getNavSectionsForFundPlatform } from "./get-nav-items-for-fund-platform";
import { useFetchImpactShopStore } from "../../store/impact-shop";
import { Footer } from "@ecologi/react-components";

const useImpactShopStore = useFetchImpactShopStore();
const footerDenyList = ["/thanks", "/login", "/email-confirmation"];

type LayoutProps = {
  children: React.ReactNode;
  hideHeader?: boolean;
  isLightHeader: boolean;
  isProfile: boolean;
  isWhiteBody: boolean;
  navColour?: string;
};

export function Layout({
  children,
  hideHeader,
  isLightHeader,
  isProfile,
  isWhiteBody,
  navColour,
  ...props
}: LayoutProps) {
  const router = useRouter();
  const {
    location,
    setLocation,
    user,
    setReferrerUserId,
    accessedFromPhoenix,
    setAccessedFromPhoenix,
  } = useStore();
  const { totalNumberOfProductsInBasket } = useImpactShopStore();
  const productsInBasket = totalNumberOfProductsInBasket();

  const currentPath = router.pathname;
  const { usernameOrId, childUsernameOrId, from } = router.query;
  const [shouldHideNavigation, setShouldHideNavigation] = useState(
    hideHeader || false
  );

  const routeUsernameOrId = childUsernameOrId || usernameOrId;
  const isAnothersProfile = user
    ? isProfile && !!routeUsernameOrId && routeUsernameOrId !== user.username
    : false;

  const isLoggedInViewingFundPlatform =
    !!user && !!isProfile && !isAnothersProfile;

  const isOnImpactShopPage = currentPath.startsWith("/impact-shop");

  useEffect(() => {
    if (router.isReady) {
      const fromPhoenix = typeof from === "string" && from === "phoenix";
      if (fromPhoenix) {
        setAccessedFromPhoenix(true);
      } else if (!isOnImpactShopPage) {
        setAccessedFromPhoenix(false);
      }
      setShouldHideNavigation(
        isOnImpactShopPage && (accessedFromPhoenix || !user)
      );
    }
  }, [
    router.isReady,
    from,
    isOnImpactShopPage,
    accessedFromPhoenix,
    setAccessedFromPhoenix,
    user,
  ]);

  const { r: referrer } = parseQueryObject(router.query);

  useEffect(() => {
    if (referrer) {
      setReferrerUserId(referrer);
    }
  }, [referrer]);

  const isFooterVisible = !footerDenyList.includes(currentPath);

  const navSections = useMemo(
    () =>
      isLoggedInViewingFundPlatform
        ? getNavSectionsForFundPlatform({ productsInBasket, user })
        : getNavSectionsForMarketingSite({ productsInBasket, user }),
    [isLoggedInViewingFundPlatform, user, isProfile, productsInBasket]
  );

  const selectedNavItem = useMemo(
    () =>
      navSections.flat().find((item) => {
        if ("links" in item) {
          return item.links.some((link) =>
            [link.path, link.route].includes(currentPath)
          );
        }
        return [item.path, item.route].includes(currentPath);
      }),
    [router.asPath, navSections]
  );

  return (
    <>
      {shouldHideNavigation ? (
        <main>
          {children}
          {isFooterVisible && (
            <Footer
              {...props}
              user={user}
              setUserLocation={setLocation}
              location={location}
            />
          )}
        </main>
      ) : (
        <NavigationDrawer
          {...props}
          currentPath={currentPath}
          displayName={user ? user.fullName() : null}
          isLight={isLightHeader}
          hideHeader={hideHeader}
          isLoggedInViewingFundPlatform={isLoggedInViewingFundPlatform}
          isOverlayNav={!isLoggedInViewingFundPlatform}
          location={location}
          navColour={navColour}
          navSections={navSections}
          selectedRootNavItemId={selectedNavItem?.id || ""}
          setUserLocation={setLocation}
        >
          {children}
          {isFooterVisible && (
            <Footer
              {...props}
              user={user}
              setUserLocation={setLocation}
              location={location}
            />
          )}
        </NavigationDrawer>
      )}
      <ToastContainer
        autoClose={4000}
        closeButton={false}
        hideProgressBar
        position="bottom-center"
        transition={Slide}
      />
      <div id="modal-root"></div>
    </>
  );
}
